/**
 * PartInspector — a pure display panel that teaches you what a structural
 * member actually is. Given a `Member`, it renders its real steel spec (area,
 * moment of inertia, slenderness, capacities), a drawn SVG of the true
 * cross-section, and a photo of the real steel product for that shape.
 *
 * It is deliberately dumb: it takes a Member and paints. The maintainer
 * raycasts a click in the 3D tower to a member and calls `show(m)`.
 */

import type { Member, MaterialKey, MaterialCategory } from "@/physics/model";
import { tensionCapacity, compressionCapacity, MATERIALS, MATERIAL_CATEGORY } from "@/physics/model";

/** The five real-world shape families we can classify a section into. */
type ShapeKind = "pipe" | "angle" | "flat" | "rod" | "guy";

interface ShapeInfo {
  kind: ShapeKind;
  /** Human label for the shape family (used near the photo). */
  label: string;
  /** Wikimedia Commons photo of the real steel product. */
  photo: string;
  /** "Datasheet / more" reference link for this shape family. */
  datasheet: string;
}

/**
 * Real, verified-existing Wikimedia Commons files (resolved via the
 * Special:FilePath redirect, which 302s to the actual upload URL). Each is a
 * genuine photo of that steel product. `img.onerror` hides the <img> so a dead
 * link degrades gracefully to just the drawn SVG cross-section.
 */
const COMMONS = "https://commons.wikimedia.org/wiki/Special:FilePath/";
const SHAPE_MEDIA: Record<ShapeKind, { photo: string; datasheet: string; label: string }> = {
  pipe: {
    label: "Round steel pipe (hollow section)",
    photo: COMMONS + encodeURIComponent("Black iron steam pipe.jpg"),
    datasheet: "https://en.wikipedia.org/wiki/Steel_pipe",
  },
  angle: {
    label: "Equal-leg angle (L-section)",
    photo: COMMONS + encodeURIComponent("Slotted Angle Storage System.jpg"),
    datasheet: "https://en.wikipedia.org/wiki/Angle_iron",
  },
  flat: {
    label: "Flat bar (rectangular stock)",
    photo: COMMONS + encodeURIComponent("Assorted bar stock.jpg"),
    datasheet: "https://en.wikipedia.org/wiki/Bar_stock",
  },
  rod: {
    label: "Solid round bar",
    photo: COMMONS + encodeURIComponent("Barre section circulaire.jpg"),
    datasheet: "https://en.wikipedia.org/wiki/Bar_stock",
  },
  guy: {
    label: "Galvanized steel wire strand (guy)",
    photo: COMMONS + encodeURIComponent("Cable (PSF).jpg"),
    datasheet: "https://en.wikipedia.org/wiki/Wire_rope",
  },
};

/**
 * Classify a member's real shape from its section name and the tension-only
 * flag. Guys are tension-only strand; the rest are read off the AISC-style
 * name prefixes used in the section catalog (`Pipe`, `L`, `Flat`, `Ø…rod`).
 */
function classifyShape(m: Member): ShapeInfo {
  const name = m.section.name;
  let kind: ShapeKind;
  if (m.tensionOnly || /EHS|strand|guy|cable/i.test(name)) {
    kind = "guy";
  } else if (/^pipe/i.test(name) || /tube|pipe/i.test(name)) {
    kind = "pipe";
  } else if (/^L\d/i.test(name) || /angle/i.test(name)) {
    kind = "angle";
  } else if (/^flat/i.test(name)) {
    kind = "flat";
  } else if (/rod|Ø|round|bar/i.test(name)) {
    kind = "rod";
  } else {
    // Sensible fallback: a solid bar drawing is the least misleading default.
    kind = "rod";
  }
  const media = SHAPE_MEDIA[kind];
  return { kind, label: media.label, photo: media.photo, datasheet: media.datasheet };
}

// --- material identity (category, swatch, emoji, blurb) ----------------------
//
// New materials in the catalog flow through the spec table automatically (it reads
// m.material.E/Fy/rho generically). This block adds a lightweight VISUAL identity —
// a colour swatch + emoji + one-liner — keyed off the material category, so the
// player recognises "this is spaghetti / bamboo / titanium" without any binary
// image assets. Brittle/novelty materials get an explicit warning.

interface MaterialIdentity {
  category: MaterialCategory;
  /** Swatch colour representative of the material family. */
  color: string;
  /** A small emoji glyph shown next to the name. */
  emoji: string;
  /** One-line plain-language blurb about the tradeoff. */
  blurb: string;
  /** True for the deliberately weak/brittle novelty materials. */
  novelty: boolean;
}

/** Per-key visual identity for known catalog materials. */
const MATERIAL_IDENTITY: Partial<Record<MaterialKey, { color: string; emoji: string; blurb: string }>> = {
  a36: { color: "#8a97a6", emoji: "🔩", blurb: "Mild structural steel — the workhorse grade." },
  a572: { color: "#8a97a6", emoji: "🔩", blurb: "High-strength low-alloy steel — the tower default." },
  a992: { color: "#8a97a6", emoji: "🔩", blurb: "Wide-flange building steel, same Fy as A572." },
  guyStrand: { color: "#b9c2cc", emoji: "🪢", blurb: "Extra-high-strength galvanized guy strand." },
  aramid: { color: "#e0c060", emoji: "🧵", blurb: "Kevlar guy — non-conductive, light, very strong." },
  al6061: { color: "#c9ced4", emoji: "🪟", blurb: "Aluminium 6061-T6 — light but soft; buckles easily." },
  ss304: { color: "#c3cdd6", emoji: "✨", blurb: "304 stainless — rustproof, steel-stiff, modest yield." },
  ti6al4v: { color: "#a7a2b0", emoji: "🚀", blurb: "Titanium — superb strength-to-weight, pricey." },
  wroughtIron: { color: "#6e6a66", emoji: "⚙️", blurb: "Wrought iron — the ductile Eiffel-era metal." },
  castIron: { color: "#4d4a48", emoji: "🕳️", blurb: "Grey cast iron — BRITTLE in tension; snaps." },
  cfrp: { color: "#2b2b2f", emoji: "🏎️", blurb: "Carbon fibre — stiff, strong AND light." },
  gfrp: { color: "#d8e3ea", emoji: "🛶", blurb: "Fibreglass — cheap, tough, but flexy." },
  douglasFir: { color: "#c79a5c", emoji: "🌲", blurb: "Timber — light and cheap, but soft and flexy." },
  oak: { color: "#a9772f", emoji: "🪵", blurb: "Oak — a denser, stronger hardwood." },
  bamboo: { color: "#9bb43a", emoji: "🎋", blurb: "Bamboo — nature's rebar; great strength-to-weight." },
  spaghetti: { color: "#e6c56a", emoji: "🍝", blurb: "Dry pasta — VERY brittle. It will snap. Have fun." },
  greenWillow: { color: "#7fae55", emoji: "🌿", blurb: "Green willow — whippy and weak; can't hold a column." },
  ice: { color: "#a9e0f2", emoji: "❄️", blurb: "Ice — BRITTLE; shatters under almost any pull." },
  glass: { color: "#bfe3e0", emoji: "🫧", blurb: "Glass — stiff but shatters; flaws govern." },
};

/**
 * Classify a member's MATERIAL back to a catalog key (by name) and return its
 * visual identity. Falls back to a neutral steel-grey identity if the material
 * isn't in the catalog (e.g. an inline custom Material), so this never throws.
 */
function classifyMaterial(m: Member): MaterialIdentity {
  const key = (Object.keys(MATERIALS) as MaterialKey[]).find(
    (k) => MATERIALS[k].name === m.material.name,
  );
  const category: MaterialCategory = key ? MATERIAL_CATEGORY[key] : "Metals";
  const id = key ? MATERIAL_IDENTITY[key] : undefined;
  return {
    category,
    color: id?.color ?? "#8a97a6",
    emoji: id?.emoji ?? "🔩",
    blurb: id?.blurb ?? `${m.material.name} — engineered structural material.`,
    novelty: category === "Novelty",
  };
}

// --- unit helpers (model is SI: m, m^2, m^4, Pa, kg/m^3) ---
const M2_TO_CM2 = 1e4; // m^2 -> cm^2
const M4_TO_CM4 = 1e8; // m^4 -> cm^4
const M_TO_MM = 1e3;
const N_TO_KN = 1e-3;
const PA_TO_GPA = 1e-9;
const PA_TO_MPA = 1e-6;

/**
 * Environmental conditions the tower is being solved under. All optional; when
 * omitted (or zero) the corresponding driver is treated as inactive and left
 * out of the explanation. Mirrors what the solver reads from the world state.
 */
export interface InspectConditions {
  /** Design wind speed (m/s, 3-s gust) driving sail-area drag. */
  windSpeed?: number;
  /** Radial ice accretion (mm): dead weight + widened wind profile. */
  iceMm?: number;
  /** Temperature change ΔT from the stress-free install temp (°C). */
  tempC?: number;
}

/** Thermal expansion coefficient of steel (per °C), matches the solver. */
const ALPHA_STEEL = 12e-6;

/** Format a number to a fixed number of significant-ish figures for display. */
function fmt(n: number, digits = 2): string {
  if (!isFinite(n)) return "∞";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1000 || abs < 0.01) {
    return n.toExponential(2);
  }
  return n.toFixed(digits);
}

/** Radius of gyration r = √(I/A) in metres. */
function radiusOfGyration(m: Member): number {
  return Math.sqrt(m.section.I / m.section.A);
}

/**
 * Build the SVG cross-section for a shape. Drawn in a 120×120 viewBox with a
 * consistent steel-blue fill; each glyph is the true section outline for that
 * family so it reads as "what the steel looks like end-on".
 */
function crossSectionSvg(kind: ShapeKind, sizeLabel: string): string {
  const steel = "#7fb2ff";
  const stroke = "#cfe0ff";
  const bg = "#11161f";
  let shape = "";
  switch (kind) {
    case "pipe":
      // Annulus (ring): outer circle minus inner circle via even-odd fill.
      shape = `
        <path fill="${steel}" stroke="${stroke}" stroke-width="1.5" fill-rule="evenodd"
          d="M60,18 a42,42 0 1,0 0.01,0 Z M60,34 a26,26 0 1,1 -0.01,0 Z"/>`;
      break;
    case "angle": {
      // L outline: two legs of equal length meeting at the corner.
      const t = 16; // leg thickness
      const o = 20,
        s = 80; // origin + leg length
      shape = `
        <polygon fill="${steel}" stroke="${stroke}" stroke-width="1.5"
          points="${o},${o} ${o + t},${o} ${o + t},${o + s - t} ${o + s},${o + s - t} ${o + s},${o + s} ${o},${o + s}"/>`;
      break;
    }
    case "flat":
      // Wide, thin rectangle (a flat bar seen on-edge in section).
      shape = `<rect x="14" y="52" width="92" height="16" rx="2"
        fill="${steel}" stroke="${stroke}" stroke-width="1.5"/>`;
      break;
    case "rod":
      // Solid filled circle.
      shape = `<circle cx="60" cy="60" r="38" fill="${steel}" stroke="${stroke}" stroke-width="1.5"/>`;
      break;
    case "guy": {
      // Stranded-cable glyph: a few twisted wires around a core.
      const core = `<circle cx="60" cy="60" r="12" fill="${steel}" stroke="${stroke}" stroke-width="1"/>`;
      const wires: string[] = [];
      const n = 6;
      for (let k = 0; k < n; k++) {
        const a = (k / n) * Math.PI * 2;
        const cx = 60 + Math.cos(a) * 22;
        const cy = 60 + Math.sin(a) * 22;
        wires.push(
          `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="11" fill="${steel}" stroke="${stroke}" stroke-width="1"/>`,
        );
      }
      shape = wires.join("") + core;
      break;
    }
  }
  return `
    <svg viewBox="0 0 120 132" width="150" height="165" role="img" aria-label="cross-section">
      <rect x="0" y="0" width="120" height="132" rx="6" fill="${bg}"/>
      ${shape}
      <text x="60" y="126" text-anchor="middle" font-size="11"
        font-family="ui-monospace,Menlo,monospace" fill="#9fb0c8">${escapeHtml(sizeLabel)}</text>
    </svg>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** One row of the spec table. */
function specRow(label: string, value: string, unit: string): string {
  return `
    <tr>
      <td style="padding:3px 10px 3px 0;color:#9fb0c8;white-space:nowrap;">${escapeHtml(label)}</td>
      <td style="padding:3px 8px 3px 0;color:#eaf1ff;text-align:right;font-family:ui-monospace,Menlo,monospace;">${escapeHtml(value)}</td>
      <td style="padding:3px 0;color:#6b7a92;">${escapeHtml(unit)}</td>
    </tr>`;
}

/** A short "why" note line inside the Loads section. */
function noteLine(text: string): string {
  return `<div style="font-size:11.5px;line-height:1.45;color:#9fb0c8;margin-top:5px;">${text}</div>`;
}

/** A key→value environmental driver row (colored accent on the value). */
function driverRow(label: string, value: string, note: string): string {
  return `
    <div style="margin-top:8px;">
      <div style="display:flex;justify-content:space-between;gap:10px;align-items:baseline;">
        <span style="font-size:12px;color:#cfe0ff;">${label}</span>
        <span style="font-size:12px;color:#eaf1ff;font-family:ui-monospace,Menlo,monospace;text-align:right;">${value}</span>
      </div>
      <div style="font-size:11px;line-height:1.4;color:#7f8ea6;margin-top:2px;">${note}</div>
    </div>`;
}

/**
 * Build the "Loads & utilization" section: explains the member's current axial
 * force vs its governing capacity (with legible arithmetic), then lists only the
 * active environmental drivers (wind / ice / temperature) and how each pushes
 * this member toward or away from its limit. Pure string builder.
 */
function loadsSection(
  m: Member,
  governs: string,
  Pc_kN: number,
  Pt_kN: number,
  utilPct: number,
  utilColor: string,
  cond?: InspectConditions,
): string {
  const isGuy = m.tensionOnly;
  const force_kN = m.force * N_TO_KN;
  const inTension = m.force >= 0;
  const absForce_kN = Math.abs(force_kN);

  // Governing capacity for THIS force sign (tension → A·Fy; comp → min limit).
  const capName = inTension
    ? isGuy
      ? "breaking strength"
      : "tension yield A·Fy"
    : `compression (${governs} governs)`;
  const capVal_kN = inTension ? Pt_kN : Pc_kN;

  // State summary line: force, direction, capacity, and the arithmetic → %.
  let stateNote: string;
  if (isGuy && m.slack) {
    stateNote = noteLine(
      `This guy is currently <b style="color:#ffcc66;">slack</b> — carrying essentially no tension, so its utilization sits near 0%. Cables only pull; they never push.`,
    );
  } else {
    const dirWord = isGuy
      ? "cable tension"
      : inTension
        ? "<b style=\"color:#7fb2ff;\">tension</b> (being pulled apart)"
        : "<b style=\"color:#ff9e6b;\">compression</b> (being squashed)";
    const arithmetic =
      capVal_kN > 0
        ? `<b style="color:${utilColor};">${fmt(absForce_kN, 0)} kN ÷ ${fmt(capVal_kN, 0)} kN = ${fmt(utilPct, 0)}%</b>`
        : `<b style="color:${utilColor};">${fmt(utilPct, 0)}%</b>`;
    stateNote = noteLine(
      `Right now this member carries <b>${fmt(absForce_kN, 0)} kN</b> of ${dirWord}. Its governing limit is ${capName} at <b>${fmt(capVal_kN, 0)} kN</b>, so utilization = |force| ÷ capacity = ${arithmetic} — the figure shown above.`,
    );
  }

  // Active environmental drivers (only list non-zero ones).
  const V = cond?.windSpeed ?? 0;
  const ice = cond?.iceMm ?? 0;
  const dT = cond?.tempC ?? 0;

  const drivers: string[] = [];

  if (V > 0) {
    // Base velocity pressure q = 0.613·V² (Kz≈1 near-ground reference).
    const q = 0.613 * V * V;
    drivers.push(
      driverRow(
        "Wind",
        `${fmt(V, 0)} m/s`,
        `Velocity pressure q = 0.613·V² = <b style="color:#cfe0ff;">${fmt(q, 0)} Pa</b>, acting as sail-area drag (q·Cd·A) on every member — more wind means more load pushed into ${isGuy ? "the guys and legs" : "this member"}.`,
      ),
    );
  }

  if (ice > 0) {
    drivers.push(
      driverRow(
        "Ice",
        `${fmt(ice, 0)} mm`,
        `Radial ice adds dead weight and widens the wind profile, so drag area grows — ${isGuy ? "cable" : "member"} load rises${isGuy ? " and guy tension climbs" : ""}.`,
      ),
    );
  }

  if (dT !== 0) {
    // Thermal pre-strain magnitude for context: ε = α·ΔT.
    const strain = ALPHA_STEEL * Math.abs(dT);
    const cold = dT < 0;
    const effect = isGuy
      ? cold
        ? "cold contracts the strand and pulls the guy <b>tighter</b> → higher tension"
        : "heat relaxes the strand → the guy loses tension"
      : cold
        ? "cold shrinks rest length, pretensioning members <b>tighter</b>"
        : "heat lengthens rest length, relaxing members";
    drivers.push(
      driverRow(
        "Temperature",
        `${dT > 0 ? "+" : ""}${fmt(dT, 0)} °C`,
        `Thermal pre-strain ε = α·ΔT = ${fmt(strain * 1e6, 0)} µε (α = 12×10⁻⁶/°C): ${effect}.`,
      ),
    );
  }

  const guyNote = isGuy
    ? noteLine(
        `As a <b>guy cable</b> this member only ever carries tension, checked against its rated breaking strength. Cold and ice both <b>raise</b> guy tension, so watch it in a winter storm.`,
      )
    : "";

  const driverBlock =
    drivers.length > 0
      ? `<div style="margin-top:10px;font-size:10.5px;text-transform:uppercase;letter-spacing:.5px;color:#6b7a92;">Active environment</div>${drivers.join("")}`
      : cond
        ? noteLine(`No active wind, ice, or temperature offset right now — this member is under gravity/pretension only.`)
        : "";

  return `
    <div style="margin-top:12px;border-top:1px solid #1a2230;padding-top:10px;">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:#6b7a92;margin-bottom:2px;">Loads &amp; utilization</div>
      ${stateNote}
      ${guyNote}
      ${driverBlock}
    </div>`;
}

export class PartInspector {
  private container: HTMLElement;
  private root: HTMLDivElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.root = document.createElement("div");
    this.root.style.display = "none";
    this.container.appendChild(this.root);
  }

  /**
   * Render the panel for a member. Replaces any prior content.
   *
   * @param m    the member to inspect.
   * @param cond optional live environment (wind / ice / temperature) used to
   *             explain WHY the member sees the load it does. Omit it and the
   *             panel still renders (old one-arg calls keep working).
   */
  show(m: Member, cond?: InspectConditions): void {
    const shape = classifyShape(m);
    const mat = classifyMaterial(m);

    const A_cm2 = m.section.A * M2_TO_CM2;
    const I_cm4 = m.section.I * M4_TO_CM4;
    const r_mm = radiusOfGyration(m) * M_TO_MM;
    const lambda = (m.Kfactor * m.L0) / radiusOfGyration(m);

    const E_GPa = m.material.E * PA_TO_GPA;
    const Fy_MPa = m.material.Fy * PA_TO_MPA;

    const Pt_kN = tensionCapacity(m) * N_TO_KN;
    const Pc_kN = compressionCapacity(m) * N_TO_KN;

    // Which limit governs compression: Euler buckling vs yield?
    const euler =
      (Math.PI * Math.PI * m.material.E * m.section.I) / Math.pow(m.Kfactor * m.L0, 2);
    const yieldCap = m.section.A * m.material.Fy;
    const governs = euler < yieldCap ? "Euler buckling" : "yield";

    const utilPct = m.util * 100;
    const utilColor = m.util >= 1 ? "#ff6b6b" : m.util >= 0.7 ? "#ffcc66" : "#6ee7a0";

    const specTable = `
      <table style="border-collapse:collapse;font-size:12.5px;width:100%;">
        ${specRow("Area A", fmt(A_cm2), "cm²")}
        ${specRow("Moment I", fmt(I_cm4), "cm⁴")}
        ${specRow("Radius of gyration r", fmt(r_mm), "mm")}
        ${specRow("Slenderness λ = K·L₀/r", fmt(lambda, 1), "")}
        ${specRow("Length L₀", fmt(m.L0), "m")}
        ${specRow("Young's modulus E", fmt(E_GPa, 0), "GPa")}
        ${specRow("Yield Fy", fmt(Fy_MPa, 0), "MPa")}
        ${specRow("Density ρ", fmt(m.material.rho, 0), "kg/m³")}
      </table>`;

    const capTable = `
      <table style="border-collapse:collapse;font-size:12.5px;width:100%;">
        ${specRow("Tension  A·Fy", fmt(Pt_kN, 1), "kN")}
        ${specRow(`Compression (${governs})`, fmt(Pc_kN, 1), "kN")}
        <tr>
          <td style="padding:6px 10px 3px 0;color:#9fb0c8;">Utilization</td>
          <td style="padding:6px 8px 3px 0;text-align:right;font-family:ui-monospace,Menlo,monospace;color:${utilColor};font-weight:600;">${fmt(utilPct, 0)}</td>
          <td style="padding:6px 0 3px 0;color:#6b7a92;">%</td>
        </tr>
      </table>`;

    const header =
      `${escapeHtml(m.section.name)} · ` +
      `<span style="white-space:nowrap;">${mat.emoji} ${escapeHtml(m.material.name)}</span>`;

    // Small material identity strip: swatch + category (+ novelty flag).
    const noveltyTag = mat.novelty
      ? `<span style="margin-left:auto;color:#f0883e;font-weight:600;">⚠ novelty</span>`
      : "";
    const materialStrip = `
      <div style="display:flex;align-items:center;gap:8px;margin-top:8px;font-size:11.5px;color:#9fb0c8;">
        <span style="width:14px;height:14px;border-radius:4px;flex:none;
          background:${mat.color};border:1px solid rgba(255,255,255,0.25);"></span>
        <span>${escapeHtml(mat.category)}</span>
        ${noveltyTag}
      </div>
      <div style="font-size:11.5px;line-height:1.45;color:#7f8ea6;margin-top:4px;">${escapeHtml(mat.blurb)}</div>`;

    this.root.innerHTML = `
      <div style="
        position:relative;width:340px;max-width:92vw;
        background:#0b0e13;color:#eaf1ff;
        border:1px solid #1e2836;border-radius:12px;
        box-shadow:0 10px 40px rgba(0,0,0,0.55);
        font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;
        padding:16px 16px 14px;box-sizing:border-box;">

        <button data-pi-close aria-label="Close" style="
          position:absolute;top:10px;right:10px;width:26px;height:26px;
          border:none;border-radius:6px;cursor:pointer;
          background:#1a2230;color:#9fb0c8;font-size:15px;line-height:1;">✕</button>

        <div style="font-size:15px;font-weight:600;letter-spacing:.2px;padding-right:30px;">
          ${header}
        </div>
        <div style="font-size:11.5px;color:#6b7a92;margin-top:2px;">${escapeHtml(shape.label)}</div>
        ${materialStrip}

        <div style="display:flex;gap:12px;margin-top:12px;align-items:flex-start;">
          <div>${crossSectionSvg(shape.kind, m.section.name)}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:#6b7a92;margin-bottom:2px;">Spec</div>
            ${specTable}
          </div>
        </div>

        <div style="margin-top:12px;border-top:1px solid #1a2230;padding-top:10px;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:#6b7a92;margin-bottom:2px;">Capacity</div>
          ${capTable}
        </div>

        ${loadsSection(m, governs, Pc_kN, Pt_kN, utilPct, utilColor, cond)}

        <div style="margin-top:12px;border-top:1px solid #1a2230;padding-top:10px;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:.6px;color:#6b7a92;margin-bottom:6px;">Real shape · ${mat.emoji} ${escapeHtml(m.material.name)}</div>
          <img data-pi-photo alt="section shape: ${escapeHtml(shape.label)}"
            src="${shape.photo}"
            style="width:100%;height:120px;object-fit:cover;border-radius:8px;border:1px solid #1a2230;display:block;background:#11161f;"/>
          <a href="${shape.datasheet}" target="_blank" rel="noopener noreferrer"
            style="display:inline-block;margin-top:8px;font-size:12px;color:#7fb2ff;text-decoration:none;">
            Datasheet / more ↗
          </a>
        </div>
      </div>`;

    const closeBtn = this.root.querySelector<HTMLButtonElement>("[data-pi-close]");
    if (closeBtn) closeBtn.addEventListener("click", () => this.hide());

    // Gracefully hide the photo if the Commons link is broken → SVG stands alone.
    const img = this.root.querySelector<HTMLImageElement>("[data-pi-photo]");
    if (img) {
      img.onerror = () => {
        img.style.display = "none";
      };
    }

    this.root.style.display = "block";
  }

  /** Hide the panel (keeps the DOM node for reuse). */
  hide(): void {
    this.root.style.display = "none";
  }

  /** Remove the panel from the DOM entirely. */
  dispose(): void {
    this.root.remove();
  }
}
